import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Briefcase, Users, Sparkles, IdCard, Loader2, ArrowRight, MessageSquare, Calendar, TrendingUp, Zap, Award, Inbox, CalendarCheck, Pause, Play, Camera, X,
} from "lucide-react";
import { StatCard } from "@/components/dashboard/DashboardLayout";
import { OnboardingChecklist } from "@/components/dashboard/OnboardingChecklist";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import VerificationStatusCard from "@/components/employer/VerificationStatusCard";
import TrialBanner from "@/components/employer/TrialBanner";
import WelcomeChecklist from "@/components/employer/WelcomeChecklist";
import FirstJobSpotlight from "@/components/employer/FirstJobSpotlight";

const EmployerDashboardV2 = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [briefs, setBriefs] = useState([]);
    const [apps, setApps] = useState(null);
    const [bookings, setBookings] = useState(null);
    const [loading, setLoading] = useState(true);
    // Iter 92 — photo-upload nudge dismissal state. Stored in localStorage
    // per user so a dismissal on one browser is remembered, but we DO
    // re-prompt if the user switches accounts.
    const dismissKey = user ? `simonara_photo_prompt_dismissed:${user.user_id}` : null;
    const [photoPromptDismissed, setPhotoPromptDismissed] = useState(
        () => dismissKey ? !!localStorage.getItem(dismissKey) : false,
    );
    const dismissPhotoPrompt = () => {
        if (dismissKey) localStorage.setItem(dismissKey, "1");
        setPhotoPromptDismissed(true);
    };

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [s, b, a, bk] = await Promise.all([
                api.get("/employer/dashboard-stats"),
                api.get("/employer/briefs"),
                api.get("/employer/applications/summary").catch(() => ({ data: { total: 0, new: 0, top_jobs: [] } })),
                api.get("/employer/scheduler/analytics").catch(() => ({ data: null })),
            ]);
            setStats(s.data);
            setBriefs(b.data.slice(0, 3));
            setApps(a.data);
            setBookings(bk.data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const pauseTeammate = async (email, reason) => {
        try {
            const { data } = await api.post("/employer/scheduler/pause-teammate", { email, reason: reason || null });
            toast.success(`Paused ${email}${reason ? ` (${reason})` : ""} until ${new Date(data.until_iso).toLocaleDateString()}.`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const resumeTeammate = async (email) => {
        try {
            await api.post("/employer/scheduler/resume-teammate", { email });
            toast.success(`Resumed ${email}.`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (loading || !stats) return <div className="py-16 flex justify-center"><Loader2 size={20} className="animate-spin text-emerald-500" /></div>;

    const firstName = (user?.name || "").split(" ")[0];
    const company = user?.company_name || "your team";

    return (
        <div data-testid="employer-dashboard-v2">
            <WelcomeChecklist />
            <FirstJobSpotlight />
            <TrialBanner />
            <div className="mb-4"><VerificationStatusCard /></div>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                <p className="overline">Hiring HQ</p>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <h1 className="font-heading font-medium text-3xl sm:text-4xl tracking-tight text-foreground">
                        {(briefs && briefs.length > 0) ? "Welcome back" : "Welcome"}{firstName ? `, ${firstName}` : ""} <span className="inline-block">👋</span>
                    </h1>
                    {user?.founding_employer && (
                        <span
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white px-3 py-1 text-[11px] uppercase tracking-wider font-bold shadow-sm"
                            data-testid="founding-employer-badge"
                            title={`Founding Employer #${user.founding_rank || ""} — permanent 12% Recruit™ rate`}
                        >
                            <Award size={12} /> Founding Employer{user.founding_rank ? ` · #${user.founding_rank}` : ""}
                        </span>
                    )}
                </div>
                <p className="mt-2 text-muted-foreground max-w-2xl">
                    Your AI-powered hiring workspace for {company}. Every job upload becomes an executive-ready Hiring Brief with your best matched Talent Passports.
                </p>
            </motion.div>

            {/* Iter 92 — hiring-manager photo prompt.
                Fires when (a) the user has no ``picture`` on their auth
                row AND (b) they haven't dismissed the nudge yet. Dismissal
                is per-user localStorage so switching accounts still gets
                the prompt. Uses candidate-response-rate framing rather
                than a scolding "please add a photo" tone. */}
            {user && !user.picture && !photoPromptDismissed && (
                <motion.div
                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                    data-testid="employer-photo-prompt"
                    className="mt-5 rounded-2xl border border-sky-500/40 bg-gradient-to-br from-sky-50 to-white dark:from-sky-500/10 dark:to-transparent p-4 flex items-start gap-4"
                >
                    <div className="w-11 h-11 rounded-full bg-sky-500/15 flex items-center justify-center shrink-0">
                        <Camera size={20} className="text-sky-600 dark:text-sky-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-semibold text-foreground">
                            Add a photo — candidates reply 20-30% more to outreach with a face
                        </div>
                        <div className="text-sm text-muted-foreground mt-0.5">
                            Your hiring-manager photo shows next to every outreach email + on your candidate-facing pages.
                            Takes 15 seconds — we auto-crop it for you.
                        </div>
                        <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                            <Link
                                to="/employer/branding"
                                data-testid="employer-photo-prompt-cta"
                                className="inline-flex items-center gap-1.5 rounded-full bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3.5 py-1.5"
                            >
                                Add my photo <ArrowRight size={12} />
                            </Link>
                            <button
                                onClick={dismissPhotoPrompt}
                                data-testid="employer-photo-prompt-dismiss"
                                className="text-xs text-muted-foreground hover:text-foreground px-2 py-1.5"
                            >
                                Later
                            </button>
                        </div>
                    </div>
                    <button
                        onClick={dismissPhotoPrompt}
                        aria-label="Dismiss"
                        data-testid="employer-photo-prompt-close"
                        className="text-muted-foreground hover:text-foreground shrink-0"
                    >
                        <X size={14} />
                    </button>
                </motion.div>
            )}

            {/* Grace-period banner: shown when the org is currently OVER
                a plan cap (e.g. grandfathered from before enforcement).
                No auto-deletion — just a visible nudge to self-serve upgrade. */}
            {(() => {
                const jobs = stats?.plan?.usage?.active_jobs;
                const briefs = stats?.plan?.usage?.hiring_briefs;
                const over = [];
                if (jobs && !jobs.unlimited && jobs.used > jobs.limit)
                    over.push(`${jobs.used} active jobs (plan allows ${jobs.limit})`);
                if (briefs && !briefs.unlimited && briefs.used > briefs.limit)
                    over.push(`${briefs.used} AI Hiring Briefs this month (plan allows ${briefs.limit})`);
                if (!over.length) return null;
                return (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                        data-testid="employer-overcap-banner"
                        className="mt-6 rounded-2xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent p-4 flex items-start gap-3 flex-wrap"
                    >
                        <div className="h-9 w-9 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0"><Award size={17} /></div>
                        <div className="flex-1 min-w-[240px]">
                            <p className="font-heading font-semibold text-sm text-foreground">
                                You&apos;re over your current plan&apos;s limits
                            </p>
                            <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                                You currently have {over.join(" and ")}. We won&apos;t delete any of your data — but new job posts and briefs are paused until you upgrade or archive some. Growth removes the cap entirely.
                            </p>
                        </div>
                        <a
                            href="/employer/billing"
                            data-testid="employer-overcap-cta"
                            className="btn-primary text-xs inline-flex items-center shrink-0"
                        >
                            View plans →
                        </a>
                    </motion.div>
                );
            })()}

            {/* Onboarding checklist */}
            <div className="mt-8">
                <OnboardingChecklist endpoint="/employer/onboarding" ctaTo="/employer/briefs" ctaLabel="Submit a new brief" testId="employer-onboarding" />
            </div>

            {/* 6 stat cards */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
                <StatCard testId="estat-active-jobs" to="/employer/jobs"
                    icon={Briefcase} label="Active Roles" value={stats.active_jobs}
                    sub={stats.processing_jobs > 0 ? `${stats.processing_jobs} still processing` : "AI Hiring Briefs ready"} accent="green" />
                <StatCard testId="estat-talent-matches" to="/employer/find-talent"
                    icon={Sparkles} label="Talent Matches" value={stats.total_matches}
                    sub="AI-scored candidates surfaced" accent="violet" />
                <StatCard testId="estat-pipeline" to="/employer/pipeline"
                    icon={Users} label="In Pipeline" value={Object.values(stats.pipeline_counts).reduce((a, b) => a + b, 0)}
                    sub={`${stats.pipeline_counts.interview} interviewing`} accent="blue" />
                <StatCard testId="estat-interviews"
                    icon={Calendar} label="Interviews" value={stats.candidates_interviewing}
                    sub={stats.pipeline_counts.offer > 0 ? `${stats.pipeline_counts.offer} at offer stage` : "Move candidates through Pipeline"} accent="amber" />
                <StatCard testId="estat-passport-requests"
                    icon={IdCard} label="Passport Requests" value={stats.passport_requests}
                    sub="Full Passports requested" accent="pink" />
                <StatCard testId="estat-briefs" to="/employer/briefs"
                    icon={TrendingUp} label="Total Briefs" value={stats.total_briefs}
                    sub="Uploaded roles all-time" accent="slate" />
            </div>

            {/* Applications tile — surfaces public Job Board applicants */}
            {apps && (apps.total > 0 || (apps.top_jobs || []).length > 0) && (
                <motion.div
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    data-testid="edash-applications-tile"
                    className="mt-8 card-soft p-6"
                >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                                <Inbox size={16} /> <p className="overline">Public Board Applications</p>
                            </div>
                            <div className="mt-3 flex items-end gap-6">
                                <div>
                                    <div className="text-3xl font-heading font-semibold text-foreground" data-testid="edash-apps-total">{apps.total}</div>
                                    <div className="text-xs text-muted-foreground">Total applicants</div>
                                </div>
                                <div>
                                    <div className="text-3xl font-heading font-semibold text-emerald-600 dark:text-emerald-400" data-testid="edash-apps-new">{apps.new}</div>
                                    <div className="text-xs text-muted-foreground">New (unreviewed)</div>
                                </div>
                            </div>
                        </div>
                        <Link to="/employer/applications" data-testid="edash-apps-open" className="btn-green text-sm">
                            Open Application Tracker <ArrowRight size={13} className="ml-1.5" />
                        </Link>
                    </div>
                    {(apps.top_jobs || []).length > 0 && (
                        <div className="mt-5 pt-5 border-t border-border">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Top jobs by applicants</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {apps.top_jobs.map((tj) => (
                                    <Link
                                        key={tj.job_id}
                                        to={`/employer/applications?job=${tj.job_id}`}
                                        data-testid={`edash-apps-top-${tj.job_id}`}
                                        className="rounded-xl border border-border bg-muted/30 hover:bg-muted/50 p-3 transition-colors"
                                    >
                                        <div className="text-sm font-semibold text-foreground line-clamp-1">{tj.title}</div>
                                        <div className="mt-1 text-xs text-muted-foreground">{tj.location}</div>
                                        <div className="mt-2 text-lg font-heading font-semibold text-emerald-600 dark:text-emerald-400">
                                            {tj.count} <span className="text-xs text-muted-foreground font-normal">applicants</span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </motion.div>
            )}

            {/* Booking Analytics Tile — weekly interviews + per-teammate load */}
            {bookings && (bookings.this_week > 0 || bookings.upcoming > 0 || (bookings.per_teammate || []).length > 0) && (() => {
                const maxLoad = Math.max(1, ...bookings.per_teammate.map((r) => r.count));
                const delta = bookings.this_week - (bookings.last_week ?? 0);
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        data-testid="edash-bookings-tile"
                        className="mt-6 card-soft p-6"
                    >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                                    <CalendarCheck size={16} /> <p className="overline">Interviews this week</p>
                                </div>
                                <div className="mt-3 flex items-end gap-6">
                                    <div>
                                        <div className="text-3xl font-heading font-semibold text-foreground" data-testid="edash-bookings-this-week">{bookings.this_week}</div>
                                        <div className="text-xs text-muted-foreground">Booked (Mon–Sun UTC)</div>
                                    </div>
                                    <div>
                                        <div className="text-3xl font-heading font-semibold text-slate-500 dark:text-slate-400" data-testid="edash-bookings-last-week">{bookings.last_week ?? 0}</div>
                                        <div className="text-xs text-muted-foreground">Last week</div>
                                    </div>
                                    <div>
                                        <div className="text-3xl font-heading font-semibold text-emerald-600 dark:text-emerald-400" data-testid="edash-bookings-upcoming">{bookings.upcoming}</div>
                                        <div className="text-xs text-muted-foreground">Upcoming total</div>
                                    </div>
                                    {delta !== 0 && (
                                        <div className="pb-1">
                                            <span
                                                data-testid="edash-bookings-delta"
                                                className={`text-xs font-semibold px-2 py-1 rounded-full ${delta > 0 ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "bg-rose-500/15 text-rose-700 dark:text-rose-400"}`}
                                            >
                                                {delta > 0 ? "▲" : "▼"} {Math.abs(delta)} vs last week
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <Link to="/employer/scheduler" data-testid="edash-bookings-open" className="btn-outline text-sm">
                                Manage availability <ArrowRight size={13} className="ml-1.5" />
                            </Link>
                        </div>
                        {(bookings.per_teammate || []).length > 0 && (() => {
                            const pausedMap = Object.fromEntries((bookings.paused_emails || []).map((p) => [p.email.toLowerCase(), p]));
                            return (
                            <div className="mt-5 pt-5 border-t border-border">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Round-robin load this week</p>
                                <div className="space-y-2">
                                    {bookings.per_teammate.map((row) => {
                                        const pauseInfo = pausedMap[row.email.toLowerCase()];
                                        return (
                                        <div
                                            key={row.email}
                                            data-testid={`edash-bookings-load-${row.email}`}
                                            className="flex items-center gap-3"
                                        >
                                            <div className="w-52 truncate text-xs font-medium flex items-center gap-1.5" title={row.email}>
                                                {pauseInfo && <Pause size={10} className="text-amber-600 shrink-0" />}
                                                <span className={`truncate ${pauseInfo ? "text-muted-foreground line-through" : "text-foreground"}`}>{row.email}</span>
                                                {pauseInfo?.reason && (
                                                    <span
                                                        data-testid={`edash-pause-reason-${row.email}`}
                                                        className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800 dark:text-amber-300 shrink-0"
                                                    >
                                                        {pauseInfo.reason}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                                <div
                                                    className={`h-2 ${pauseInfo ? "bg-amber-400/60" : "bg-emerald-500"}`}
                                                    style={{ width: `${(row.count / maxLoad) * 100}%` }}
                                                />
                                            </div>
                                            <div className="w-8 text-right text-xs font-semibold text-foreground">{row.count}</div>
                                            {pauseInfo && (
                                                <button
                                                    onClick={() => resumeTeammate(row.email)}
                                                    data-testid={`edash-resume-${row.email}`}
                                                    title={`Paused until ${new Date(pauseInfo.until_iso).toLocaleString()}`}
                                                    className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-[10px] font-semibold hover:bg-amber-500/20"
                                                >
                                                    <Play size={9} /> Resume
                                                </button>
                                            )}
                                        </div>
                                        );
                                    })}
                                </div>
                                {bookings.per_teammate.every((r) => r.count === 0) && (
                                    <p className="mt-3 text-xs text-muted-foreground">No interviews booked yet this week — new bookings will rotate across the team above.</p>
                                )}
                                {bookings.fairness_warning && !pausedMap[bookings.fairness_warning.heaviest_email.toLowerCase()] && (
                                    <div
                                        data-testid="edash-fairness-warning"
                                        className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 flex items-start justify-between gap-3 flex-wrap"
                                    >
                                        <div className="flex items-start gap-2 min-w-0 flex-1">
                                            <TrendingUp size={14} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                            <div className="text-xs text-amber-800 dark:text-amber-300 min-w-0">
                                                <strong>Round-robin skew:</strong> {bookings.fairness_warning.heaviest_email} is carrying <strong>{bookings.fairness_warning.ratio}×</strong> the load of {bookings.fairness_warning.lightest_email} ({bookings.fairness_warning.heaviest_count} vs {bookings.fairness_warning.lightest_count}).
                                            </div>
                                        </div>
                                        <FairnessPauseButton
                                            email={bookings.fairness_warning.heaviest_email}
                                            onPause={pauseTeammate}
                                        />
                                    </div>
                                )}
                            </div>
                            );
                        })()}
                        {(bookings.no_show_alerts || []).length > 0 && (
                            <div className="mt-5 pt-5 border-t border-border" data-testid="edash-no-show-alerts">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">No-show alerts (last 90 days)</p>
                                <div className="space-y-1.5">
                                    {bookings.no_show_alerts.map((n) => (
                                        <div
                                            key={n.email}
                                            data-testid={`edash-no-show-${n.email}`}
                                            className="flex items-center justify-between gap-3 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="text-xs font-semibold text-foreground truncate">{n.name || n.email}</div>
                                                <div className="text-[10px] text-muted-foreground truncate">{n.email}</div>
                                            </div>
                                            {Array.isArray(n.trend) && n.trend.length > 0 && (
                                                <NoShowSparkline
                                                    values={n.trend}
                                                    totals={n.trend_totals || []}
                                                    testid={`edash-no-show-spark-${n.email}`}
                                                />
                                            )}
                                            <div className="text-xs font-heading font-semibold text-rose-700 dark:text-rose-400 whitespace-nowrap">
                                                {n.no_shows}/{n.total} · {Math.round(n.rate * 100)}%
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </motion.div>
                );
            })()}

            {/* Team Availability Calendar — weekly grid (Mon–Sun) of load + pause overlay. */}
            {bookings && (bookings.team_calendar || []).length > 0 && (
                <TeamAvailabilityCalendar
                    rows={bookings.team_calendar}
                    weekStart={bookings.week_start}
                    pausedMap={Object.fromEntries((bookings.paused_emails || []).map((p) => [p.email.toLowerCase(), p]))}
                    onResume={resumeTeammate}
                />
            )}

            {/* Quick actions */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="rounded-2xl bg-brand-navy dark:bg-slate-900 text-white p-7 relative overflow-hidden">
                    <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-emerald-500/20 blur-3xl" />
                    <div className="relative">
                        <div className="flex items-center gap-2">
                            <Zap size={16} className="text-emerald-400" />
                            <p className="overline !text-brand-green">Post a new role</p>
                        </div>
                        <h3 className="mt-2 font-heading font-semibold text-xl">Get 5 matched Talent Passports in minutes.</h3>
                        <p className="mt-1.5 text-sm text-white/70 max-w-md">Upload a JD (URL, PDF, or paste). Simonara AI parses it, matches to our Talent Passport library, and drafts an executive brief.</p>
                        <Link to="/employer/briefs#new" data-testid="edash-new-brief" className="btn-green mt-5 inline-flex text-sm">
                            Create Hiring Brief <ArrowRight size={14} className="ml-2" />
                        </Link>
                    </div>
                </div>
                <div className="card-soft p-7">
                    <MessageSquare size={18} className="text-emerald-500" />
                    <h3 className="mt-3 font-heading font-semibold text-lg text-foreground">Message candidates directly</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground">Every match on Simonara can be reached through your Inbox — no email tag, no scheduling ping-pong.</p>
                    <Link to="/inbox" className="btn-outline mt-4 inline-flex text-sm">Open Inbox <ArrowRight size={13} className="ml-2" /></Link>
                </div>
            </div>

            {/* Recent briefs */}
            {briefs.length > 0 && (
                <div className="mt-10">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-heading font-semibold text-lg text-foreground">Recent Hiring Briefs</h2>
                        <Link to="/employer/briefs" className="text-xs font-semibold text-brand-green hover:underline">View all →</Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {briefs.map((b) => (
                            <Link key={b.id} to="/employer/briefs" data-testid={`edash-brief-${b.id}`} className="card-soft p-5 block hover:border-emerald-500/40 transition-colors">
                                <div className="flex items-start justify-between gap-2">
                                    <h3 className="font-heading font-semibold text-foreground line-clamp-2">{b.job_profile?.title || "Analyzing…"}</h3>
                                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${b.status === "ready" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "bg-amber-500/15 text-amber-600"}`}>{b.status}</span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1.5">{b.job_profile?.company} · {b.job_profile?.location}</div>
                                <div className="mt-4 text-xs text-muted-foreground">{(b.top_matches || []).length} candidate{(b.top_matches || []).length === 1 ? "" : "s"} matched</div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployerDashboardV2;

// ---------- Sub-components -------------------------------------------------

// 4-week trend of no-show rate. Weeks with zero bookings render as a hollow
// dot on the baseline so users can see the candidate had no interviews that
// week (vs. attended everything).
const NoShowSparkline = ({ values, totals, testid }) => {
    const W = 60;
    const H = 22;
    const step = values.length > 1 ? W / (values.length - 1) : W;
    const points = values.map((v, i) => {
        const x = i * step;
        const y = H - (v * (H - 4)) - 2; // inset 2px top/bottom
        return { x, y, hollow: (totals[i] ?? 0) === 0 };
    });
    const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
    return (
        <svg
            width={W}
            height={H}
            viewBox={`0 0 ${W} ${H}`}
            data-testid={testid}
            className="shrink-0"
            role="img"
            aria-label={`4-week no-show trend, values ${values.map((v) => Math.round(v * 100) + "%").join(", ")}`}
        >
            <line x1="0" y1={H - 2} x2={W} y2={H - 2} stroke="currentColor" strokeOpacity="0.15" strokeWidth="1" />
            <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-rose-600 dark:text-rose-400" />
            {points.map((p, i) => (
                <circle
                    key={i}
                    cx={p.x}
                    cy={p.y}
                    r={2}
                    fill={p.hollow ? "transparent" : "currentColor"}
                    stroke="currentColor"
                    strokeWidth="1"
                    className="text-rose-600 dark:text-rose-400"
                />
            ))}
        </svg>
    );
};

// Weekly grid — rows = teammates, cols = Mon–Sun. Cell intensity scales with
// load, amber wash for paused days. Cells are clickable — opens a drill-down
// modal listing the exact bookings for that (teammate, day) cell.
const TeamAvailabilityCalendar = ({ rows, weekStart, pausedMap = {}, onResume }) => {
    const maxLoad = Math.max(1, ...rows.flatMap((r) => r.days.map((d) => d.count)));
    const weekStartDate = new Date(weekStart);
    const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const [drill, setDrill] = useState(null); // { email, date, bookings, loading }

    const openDrill = async (email, date) => {
        setDrill({ email, date, bookings: null, loading: true });
        try {
            const { data } = await api.get("/employer/scheduler/bookings?include_past=true");
            const all = [...(data.results || []), ...(data.past || [])];
            const filtered = all.filter((b) => {
                if (!b.start_iso) return false;
                const bDate = b.start_iso.slice(0, 10);
                const assignee = (b.assigned_email || "").toLowerCase();
                return bDate === date && assignee === email.toLowerCase();
            }).sort((a, b) => a.start_iso.localeCompare(b.start_iso));
            setDrill({ email, date, bookings: filtered, loading: false });
        } catch { setDrill({ email, date, bookings: [], loading: false }); }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            data-testid="edash-team-calendar"
            className="mt-6 card-soft p-6"
        >
            <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
                <div>
                    <div className="flex items-center gap-2 text-indigo-700 dark:text-indigo-400">
                        <Calendar size={16} /> <p className="overline">Team availability this week</p>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">Bookings per teammate per day (UTC). Click a cell for the exact bookings. Amber = paused.</p>
                </div>
                <div className="hidden sm:flex items-center gap-3 text-[10px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-200 dark:bg-emerald-900" /> Light</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500" /> Heavy</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-400/60" /> Paused</span>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr>
                            <th className="text-left font-medium text-muted-foreground pb-2 pr-3 w-52">Teammate</th>
                            {dayLabels.map((lbl, i) => {
                                const d = new Date(weekStartDate.getTime() + i * 24 * 3600 * 1000);
                                return (
                                    <th key={lbl} className="text-center font-medium text-muted-foreground pb-2 min-w-[42px]">
                                        <div>{lbl}</div>
                                        <div className="text-[9px] font-normal opacity-70">{d.getDate()}</div>
                                    </th>
                                );
                            })}
                            <th className="text-right font-medium text-muted-foreground pb-2 pl-3 w-16">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const total = row.days.reduce((a, b) => a + b.count, 0);
                            const pauseInfo = pausedMap[row.email.toLowerCase()];
                            return (
                                <tr key={row.email} data-testid={`edash-calendar-row-${row.email}`} className="border-t border-border">
                                    <td className="py-2 pr-3 text-foreground font-medium truncate max-w-[13rem]" title={row.email}>
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            {row.is_paused && <Pause size={9} className="text-amber-600 shrink-0" />}
                                            <span className={row.is_paused ? "line-through text-muted-foreground" : ""}>{row.email}</span>
                                            {pauseInfo?.reason && (
                                                <span
                                                    data-testid={`edash-calendar-reason-${row.email}`}
                                                    className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800 dark:text-amber-300"
                                                >
                                                    {pauseInfo.reason}
                                                </span>
                                            )}
                                            {row.is_paused && (
                                                <button
                                                    onClick={() => onResume(row.email)}
                                                    data-testid={`edash-calendar-resume-${row.email}`}
                                                    className="ml-1 text-[9px] font-semibold text-amber-700 dark:text-amber-400 hover:underline"
                                                >
                                                    resume
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    {row.days.map((d, i) => {
                                        const intensity = d.count > 0 ? Math.min(1, d.count / maxLoad) : 0;
                                        const bg = d.paused
                                            ? "rgba(251, 191, 36, 0.35)"
                                            : intensity > 0
                                                ? `rgba(16, 185, 129, ${0.15 + intensity * 0.75})`
                                                : "transparent";
                                        return (
                                            <td key={i} className="py-1 px-0.5 text-center align-middle">
                                                <button
                                                    type="button"
                                                    onClick={() => d.count > 0 && openDrill(row.email, d.date)}
                                                    disabled={d.count === 0}
                                                    data-testid={`edash-calendar-cell-${row.email}-${i}`}
                                                    className="mx-auto rounded-md h-8 flex items-center justify-center text-[10px] font-semibold text-foreground disabled:cursor-default enabled:hover:ring-2 enabled:hover:ring-emerald-500/40 transition-shadow"
                                                    style={{ backgroundColor: bg, minWidth: "36px" }}
                                                    title={`${d.date}${d.paused ? " (paused)" : ""} — ${d.count} booking${d.count === 1 ? "" : "s"}${d.count > 0 ? " · click for details" : ""}`}
                                                >
                                                    {d.count > 0 ? d.count : (d.paused ? "·" : "")}
                                                </button>
                                            </td>
                                        );
                                    })}
                                    <td className="py-2 pl-3 text-right font-heading font-semibold text-foreground">{total}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <CalendarDrillDialog drill={drill} onClose={() => setDrill(null)} />
        </motion.div>
    );
};

// Drill-down list of bookings for one (teammate, day) cell.
const CalendarDrillDialog = ({ drill, onClose }) => {
    const open = drill !== null;
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="edash-calendar-drill-dialog" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-base flex items-center gap-2">
                        <Calendar size={14} className="text-emerald-500" />
                        Bookings on {drill?.date}
                    </DialogTitle>
                    <DialogDescription>
                        With <span className="font-semibold text-foreground">{drill?.email}</span>
                    </DialogDescription>
                </DialogHeader>
                {drill?.loading ? (
                    <div className="py-8 flex justify-center text-muted-foreground text-xs">
                        <Loader2 size={14} className="animate-spin mr-2" /> Loading…
                    </div>
                ) : (
                    <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                        {(drill?.bookings || []).length === 0 ? (
                            <p className="text-xs text-muted-foreground py-4 text-center">No bookings found for this cell.</p>
                        ) : (
                            drill.bookings.map((b) => (
                                <div
                                    key={b.id}
                                    data-testid={`edash-calendar-drill-row-${b.id}`}
                                    className="rounded-lg border border-border bg-card p-3 flex items-start justify-between gap-2"
                                >
                                    <div className="min-w-0">
                                        <div className="text-xs font-semibold text-foreground truncate">{b.candidate_name || b.candidate_email}</div>
                                        <div className="text-[10px] text-muted-foreground truncate">{b.candidate_email}</div>
                                        <div className="mt-1 text-[10px] text-muted-foreground">
                                            {new Date(b.start_iso).toLocaleString(undefined, { hour: "numeric", minute: "2-digit", timeZoneName: "short" })}
                                            {" · "}
                                            {b.location_type || "video"}
                                        </div>
                                    </div>
                                    {b.no_show && (
                                        <span className="inline-flex shrink-0 items-center rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-400 px-1.5 py-0.5 text-[9px] font-semibold">No-show</span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

// Fairness banner CTA — Pause Dropdown with reason presets. Falls back to
// "No reason" if the user just wants to pause without tagging.
const FairnessPauseButton = ({ email, onPause }) => {
    const REASONS = ["PTO", "Heavy sprint", "Sick", "Interview overload"];
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    data-testid="edash-fairness-pause"
                    className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 text-xs font-semibold whitespace-nowrap shrink-0"
                >
                    <Pause size={11} /> Pause until Sunday
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Reason (optional)</DropdownMenuLabel>
                {REASONS.map((r) => (
                    <DropdownMenuItem
                        key={r}
                        data-testid={`edash-pause-reason-opt-${r.replace(/\s+/g, "-").toLowerCase()}`}
                        onSelect={() => onPause(email, r)}
                        className="text-xs cursor-pointer"
                    >
                        {r}
                    </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    data-testid="edash-pause-reason-opt-none"
                    onSelect={() => onPause(email, "")}
                    className="text-xs cursor-pointer text-muted-foreground"
                >
                    No reason
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
